import { useEffect } from "react";

/**
 * Lightweight SEO helper: sets <title>, meta description, canonical, OG, and injects a JSON-LD block.
 * No dependencies — direct document.head manipulation, cleaned up on unmount.
 */
export default function SEO({ title, description, canonical, ogImage, jsonLd }) {
    useEffect(() => {
        if (title) document.title = title;

        const upsertMeta = (attr, key, value) => {
            if (!value) return null;
            let el = document.head.querySelector(`meta[${attr}="${key}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            el.setAttribute("content", value);
            return el;
        };

        const upsertLink = (rel, href) => {
            if (!href) return null;
            let el = document.head.querySelector(`link[rel="${rel}"]`);
            if (!el) {
                el = document.createElement("link");
                el.setAttribute("rel", rel);
                document.head.appendChild(el);
            }
            el.setAttribute("href", href);
            return el;
        };

        const created = [];
        created.push(upsertMeta("name", "description", description));
        created.push(upsertMeta("property", "og:title", title));
        created.push(upsertMeta("property", "og:description", description));
        if (ogImage) created.push(upsertMeta("property", "og:image", ogImage));
        created.push(upsertMeta("name", "twitter:card", "summary_large_image"));

        const href = canonical || (typeof window !== "undefined" ? window.location.origin + window.location.pathname : undefined);
        upsertLink("canonical", href);
        upsertMeta("property", "og:url", href);

        let script;
        if (jsonLd) {
            script = document.createElement("script");
            script.type = "application/ld+json";
            script.text = JSON.stringify(jsonLd);
            document.head.appendChild(script);
        }
        return () => { if (script) document.head.removeChild(script); };
    }, [title, description, canonical, ogImage, jsonLd]);
    return null;
}

export const LOCAL_BUSINESS_LD = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Final Cut Technologies",
    "description": "Commercial technology installation, integration and ongoing support for restaurants, hotels, clinics, retail and offices across Metro Vancouver.",
    "areaServed": [
        { "@type": "City", "name": "Vancouver" },
        { "@type": "City", "name": "Burnaby" },
        { "@type": "City", "name": "Richmond" },
        { "@type": "City", "name": "Surrey" },
        { "@type": "City", "name": "Coquitlam" },
        { "@type": "City", "name": "Port Moody" },
        { "@type": "City", "name": "New Westminster" },
        { "@type": "City", "name": "North Vancouver" },
        { "@type": "City", "name": "West Vancouver" },
        { "@type": "City", "name": "Delta" },
        { "@type": "City", "name": "Langley" },
        { "@type": "City", "name": "White Rock" },
    ],
    "address": { "@type": "PostalAddress", "addressRegion": "BC", "addressCountry": "CA", "addressLocality": "Metro Vancouver" },
    "telephone": "+1-604-671-4247",
    "slogan": "The technology partner behind better-run businesses.",
    "knowsAbout": [
        "Commercial AV installation",
        "Business Wi-Fi and networking",
        "Digital signage and menu boards",
        "POS hardware coordination",
        "Commercial audio systems",
        "Cable and equipment organization",
        "Managed technology support",
    ],
};

export const breadcrumbLd = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((it, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": it.name,
        "item": it.href,
    })),
});

export const serviceLd = (name, description, url) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": name,
    "description": description,
    "provider": { "@type": "ProfessionalService", "name": "Final Cut Technologies" },
    "areaServed": "Metro Vancouver, British Columbia, Canada",
    "url": url,
});

export const faqLd = (items) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
});
